import React, { Component } from "react";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { defined } from "@formant/core/common/defined";
import { narrowError } from "@formant/core/common/narrowError";
import { ResponseError } from "@formant/core/common/ResponseError";
import { IOrganization } from "@formant/core/model/IOrganization";
import { IValidation } from "@formant/core/model/IValidation";
import { IWebhook } from "@formant/core/model/IWebhook";
import { IWebhookPayload } from "@formant/core/model/IWebhookPayload";

import { organizationStore } from "../../../stores";
import { ButtonsBar } from "../../common/ButtonsBar";
import { Form } from "../../common/Form";
import { Heading3 } from "../../common/Heading3";
import { Help } from "../../common/Help";
import { Section } from "../../common/Section";
import { StandardButton } from "../../common/StandardButton";
import { Table } from "../../common/Table";
import { AddButton } from "../../configuration/AddButton";
import { WebhooksRow } from "./WebhooksRow";
import * as styles from "./WebhooksSection.scss";

export interface IWebhooksSectionProps {
    organization: IOrganization;
}

@observer
export class WebhooksSection extends Component<IWebhooksSectionProps> {
    @observable
    private webhooks: IWebhook[] = [];

    @observable
    private validation: IValidation = {};

    @observable
    private dirty: boolean = false;

    @observable
    private loading: boolean = false;

    public componentDidMount() {
        const { webhooksInfo } = this.props.organization;
        this.webhooks = (webhooksInfo && webhooksInfo.webhooks) || [];
    }

    public render() {
        const {
            dirty,
            webhooks,
            validation,
            onAdd,
            onEdit,
            onDelete,
            loading,
            onSubmit,
            onLoading,
        } = this;
        const rows = webhooks.map((_, index) => (
            <WebhooksRow
                key={index}
                webhook={_}
                index={index}
                onEdit={onEdit}
                onDelete={onDelete}
                validation={validation}
            />
        ));

        const example: IWebhookPayload = {
            eventType: "alert",
            payload: {
                id: "e0be1601-0ac2-41f8-b239-1dbe9a2d68db",
                message: "Oven temperature high",
                severity: "warning",
                streamName: "oven.temperature_c",
                streamType: "numeric",
                tags: {
                    location: "sf",
                },
                value: 250,
                time: "2019-03-09T00:13:22.903Z",
                deviceId: "c281e95e-c8f1-41b4-b573-e9138eab8900",
                sourceUrl:
                    "https://app.formant.io/events/e0be1601-0ac2-41f8-b239-1dbe9a2d68db",
            },
        };

        return (
            <Section
                title="Webhooks"
                className={styles["webhooks-section"]}
                actions={
                    <>
                        <AddButton
                            className={styles.connect}
                            label="Connect"
                            onClicked={onAdd}
                            count={webhooks.length}
                            max={5}
                            tooltips={["Connect webhook", "Maximum 5 webhooks"]}
                        />
                        <Help
                            text="Trigger external HTTP service endpoints in realtime for Formant events"
                            to="https://docs.formant.io/docs/trigger-webhooks-from-events"
                        />
                    </>
                }
            >
                <Form onSubmit={onSubmit} onLoading={onLoading}>
                    {rows.length > 0 && (
                        <Table className={styles.table}>
                            <thead>
                                <tr>
                                    <th className={styles.name}>
                                        <div>Name</div>
                                    </th>
                                    <th className={styles.url}>
                                        <div>Url</div>
                                    </th>
                                    <th className={styles.action} />
                                </tr>
                            </thead>
                            <tbody>{rows}</tbody>
                        </Table>
                    )}
                    <div className={styles.example}>
                        <Heading3>Payload Example</Heading3>
                        <code>
                            {JSON.stringify(example, undefined, "    ")}
                        </code>
                    </div>
                    <ButtonsBar fixed={true} collapsed={!dirty}>
                        <StandardButton
                            primary={true}
                            large={true}
                            loading={loading}
                        >
                            Save
                        </StandardButton>
                    </ButtonsBar>
                </Form>
            </Section>
        );
    }

    private onAdd = () => {
        this.webhooks.unshift({ name: "", url: "" });
        this.validation = {};
        this.dirty = true;
    };

    private onEdit = (index: number, webhook: IWebhook) => {
        this.webhooks[index] = webhook;
        this.dirty = true;
    };

    private onDelete = (index: number) => {
        this.webhooks.splice(index, 1);
        this.validation = {};
        this.dirty = true;
    };

    private onSubmit = async () => {
        const { organization } = this.props;
        const { webhooks } = this;

        this.validation = {};
        this.loading = true;

        try {
            await organizationStore.update(defined(organization.id), {
                webhooksInfo: { webhooks },
            });
            this.dirty = false;
        } catch (error) {
            const responseError = narrowError(
                error,
                ResponseError,
                _ => _.statusCode === 422,
            );
            this.validation = responseError.validationErrors;
        } finally {
            this.loading = false;
        }
    };

    private onLoading = (loading: boolean) => {
        this.loading = loading;
    };
}
